/// <amd-module name="VSSUI/Components/PickList/PickListDropdown" />

import { Callout } from "OfficeFabric/Callout";
import { DirectionalHint } from "OfficeFabric/common/DirectionalHint";
import { SelectionMode } from "OfficeFabric/Selection";
import { TooltipHost, TooltipOverflowMode } from "OfficeFabric/Tooltip";
import { BaseComponent } from "OfficeFabric/Utilities";
import * as React from "react";
import { getId } from "VSS/Core/Util/Object";
import { format } from "VSS/Core/Util/String";
import { FocusZoneContext } from "VSSUI/FocusZone";
import { ItemIndicatorSet } from "VSSUI/ItemIndicator";
import { css, getSafeId, KeyCode } from "VSSUI/Util";
import { IVssIconProps, VssIcon, VssIconType } from "VSSUI/VssIcon";
import { PickList } from "../../Components/PickList/PickList";
import { IPickList, IPickListDropdown, IPickListDropdownProps, IPickListSelection } from "../../Components/PickList/PickList.Props";

import "VSS/LoaderPlugins/Css!VSSUI/Components/PickList/PickListDropdown";

const DEFAULT_PICKLIST_WIDTH = 200;
const DEFAULT_VIRTUALIZATION_LIMIT = 50;

/** @deprecated */
export interface IPickListDropdownState {
    isListOpen: boolean;
    pickListWidth?: number;
    selectedItems?: any[];
}

/** @deprecated */
export class PickListDropdown<T = any> extends BaseComponent<IPickListDropdownProps<T>, IPickListDropdownState> implements IPickListDropdown {
    private _dropdown: HTMLDivElement;
    private _dropdownTitle: HTMLDivElement;
    private _pickList: IPickList;
    private _shouldRefocus: boolean; // We don't want to re-render when this changes, so making it an instance variable instead of on state
    private _id: string;
    private _maintainsOwnState: boolean;

    constructor(props: IPickListDropdownProps<T>) {
        super(props);
        this._id = getId("PickListDropdown");
        this._maintainsOwnState = false;

        if (props.initiallySelectedItems && props.selectedItems) {
            console.warn("Please only provide ONE of initiallySelectedItems OR selectedItems as props");
        }
        if (props.initiallySelectedItems) {
            this._maintainsOwnState = true;
        }

        this.state = {
            isListOpen: false,
            pickListWidth: props.pickListWidth || props.width || DEFAULT_PICKLIST_WIDTH,
            selectedItems: this._maintainsOwnState ? props.initiallySelectedItems : undefined
        };

        this._shouldRefocus = true;
    }

    public focus(): void {
        if (this._getEffectiveIsListOpen()) {
            this._pickList.focus();
        } else {
            this._dropdownTitle.focus();
        }
    }

    public forceUpdate(): void {
        if (this._pickList) {
            this._pickList.forceUpdate();
        }
    }

    public render(): JSX.Element {
        const {
            className,
            pickListClassName,
            placeholder,
            getPickListItems,
            disabled,
            selectionMode,
            preventDismissOnScroll = true,
            titleTooltipFormat,
            tabIndex,
            hideSelectedItemIcon,
            dropdownIconProps = {
                iconType: VssIconType.fabric,
                iconName: "ChevronDown"
            },
            directionalHint,
            onRenderSelectedItemText,
            id,
            onFocus,
            excludeFocusZone
        } = this.props;

        const selectedItems = this._maintainsOwnState ? this.state.selectedItems : this.props.selectedItems;

        const isListOpen = this._getEffectiveIsListOpen();
        const hasSelectedItems = selectedItems && selectedItems.length > 0;
        const titleText = hasSelectedItems ? this._getTitleText(selectedItems) : placeholder;
        const titleTooltipText = hasSelectedItems && titleTooltipFormat ? format(titleTooltipFormat, titleText) : titleText;

        const fullClassName = css("vss-PickListDropdown", className);
        const fullPickListClassName = css("vss-PickListDropdown--list", pickListClassName);

        let placeholderLabel: JSX.Element;
        if (this.props.showPlaceholderAsLabel && placeholder && hasSelectedItems) {
            placeholderLabel = <span className="vss-PickListDropdown--title-text vss-PickListDropdown--placeholder-label">{`${placeholder}: `}</span>;
        }

        let ariaLabel = placeholderLabel ? `${placeholder}: ${titleText}` : titleText;
        if (this.props.ariaLabelFormat) {
            ariaLabel = format(this.props.ariaLabelFormat, hasSelectedItems ? titleText : "");
        }

        if (isListOpen) {
            this._shouldRefocus = true;
        }

        let iconProps: IVssIconProps = undefined;
        if (hasSelectedItems && !hideSelectedItemIcon) {
            if (selectionMode === SelectionMode.multiple) {
                iconProps = {
                    iconType: VssIconType.fabric,
                    iconName: "CheckMark",
                    className: "vss-PickListDropdown--title-checkIcon"
                };
            } else {
                iconProps = this._getIconFromItem(selectedItems[0]);
            }
        }

        const pickListId = id || getId("PickList");
        const style: React.CSSProperties = {};
        if (this.props.width) {
            style.width = this.props.width + "px";
        }
        return (
            <div className={fullClassName} ref={this._resolveRef("_dropdown")} style={style}>
                <FocusZoneContext.Consumer>
                    {(zoneContext): JSX.Element => {
                        return (
                            <div
                                tabIndex={disabled ? -1 : tabIndex || 0}
                                ref={element => (this._dropdownTitle = element)}
                                onClick={this._onClick}
                                onKeyDown={this._onKeyDown}
                                className={css(
                                    "vss-PickListDropdown--title",
                                    !hasSelectedItems && "vss-PickListDropdown--placeholder",
                                    disabled && "disabled",
                                    isListOpen && "focused"
                                )}
                                aria-expanded={isListOpen}
                                aria-label={ariaLabel}
                                aria-describedby={getSafeId(this.props.ariaDescribedBy)}
                                aria-disabled={!!disabled}
                                data-focuszone={!disabled && !excludeFocusZone && zoneContext.focuszoneId}
                                role="button"
                                id={getSafeId(this._id)}
                                aria-owns={isListOpen ? getSafeId(pickListId) : null}
                                onFocus={onFocus}
                                onBlur={this._onBlur}
                            >
                                {iconProps && <VssIcon {...iconProps} />}
                                <div className="vss-PickListDropdown--title-textContainer">
                                    <TooltipHost
                                        overflowMode={hasSelectedItems && titleTooltipFormat ? undefined : TooltipOverflowMode.Parent}
                                        content={titleTooltipText}
                                    >
                                        {placeholderLabel}
                                        <span
                                            className={css(
                                                "vss-PickListDropdown--title-text",
                                                hasSelectedItems && "vss-PickListDropdown--title-text-withSelections"
                                            )}
                                        >
                                            {onRenderSelectedItemText ? onRenderSelectedItemText(selectedItems) : <>{titleText}</>}
                                        </span>
                                    </TooltipHost>
                                </div>
                                <VssIcon
                                    {...dropdownIconProps}
                                    className={css(dropdownIconProps.className, "vss-PickListDropdown--title-caretIcon")}
                                />
                                {this.getIndicators(selectedItems)}
                            </div>
                        );
                    }}
                </FocusZoneContext.Consumer>
                {isListOpen && (
                    <Callout
                        className="vss-PickListDropdown--callout"
                        onDismiss={this._onDismiss}
                        onPositioned={this._onPositioned}
                        target={this._dropdown}
                        isBeakVisible={false}
                        preventDismissOnScroll={preventDismissOnScroll}
                        gapSpace={0}
                        directionalHint={directionalHint !== undefined ? directionalHint : DirectionalHint.bottomLeftEdge}
                    >
                        <PickList
                            {...this.props}
                            initiallySelectedItems={undefined} // We don't want the PL to be uncontrolled
                            selectedItems={selectedItems}
                            width={this.state.pickListWidth}
                            className={fullPickListClassName}
                            onSelectionChanged={this._onSelectionChanged}
                            ref={(pickList: PickList) => (this._pickList = pickList)}
                            items={getPickListItems()}
                            onGroupLoadRequest={this.props.onGroupLoadRequest}
                            onFocusLost={this._onPickListFocusLost}
                            id={pickListId}
                            shouldVirtualize={items => {
                                // Virtualization is disabled for the number of items below certain limit since callout positioning happens
                                // before new pages are added which ends up a wrong callout position.
                                //
                                // Scenario is the space below the target may be sufficient for the first page and callout is
                                // positioned below target. Once the other pages are added, callout needs to be repositioned
                                // to be next to or even on top of target which does not happen.
                                //
                                // By specifying a high limit we make sure that positioning will take as much space as possible so that
                                // no repositioning is required.
                                return (items || []).length > DEFAULT_VIRTUALIZATION_LIMIT;
                            }}
                        />
                    </Callout>
                )}
            </div>
        );
    }

    public componentDidMount() {
        if (this.props.autoFocus) {
            this.focus();
        }
    }

    private _getTitleText(selectedItems: any[]) {
        let text = this._getNameFromItem(selectedItems[0]);
        if (selectedItems.length > 1) {
            text = `${text} (+${selectedItems.length - 1})`;
        }
        return text;
    }

    private getIndicators(selectedItems: any[]): JSX.Element {
        const { selectedItemIndicators } = this.props;
        if (selectedItems && selectedItems.length && selectedItemIndicators && selectedItemIndicators.length) {
            return <ItemIndicatorSet indicators={selectedItemIndicators.map(indicator => indicator.getItemIndicator(selectedItems[0]))} />;
        } else {
            return null;
        }
    }

    private _onBlur = () => {
        if (!this._getEffectiveIsListOpen()) {
            if (this.props.onFocusLost) {
                this.props.onFocusLost();
            }
        }
    };

    private _onClick = () => {
        if (!this.props.disabled) {
            this._togglePickList(!this._getEffectiveIsListOpen());
        }
    };

    private _onKeyDown = (ev: React.KeyboardEvent<HTMLElement>): void => {
        if (!this.props.disabled) {
            if (ev.which === KeyCode.enter || ev.which === KeyCode.downArrow) {
                this._togglePickList(!this._getEffectiveIsListOpen());
                ev.preventDefault();
            } else if (ev.which === KeyCode.escape) {
                this._togglePickList(false);
                this._dropdownTitle.focus();
            }
        }
    };

    private _onPickListFocusLost = (): void => {
        this._shouldRefocus = true;
        this._togglePickList(false);
    };

    private _onPositioned = () => {
        if (this._shouldRefocus) {
            this._shouldRefocus = false;
            if (this._pickList) {
                this._pickList.focus();
            }
        }
    };

    private _onDismiss = (ev: React.KeyboardEvent<HTMLElement>) => {
        // Don't dismiss on resize even though Callout wants to. It's not necessary,
        // and on mobile "resize" happens when Screen Keyboard shows up, making the picker unusable.
        if (ev.type === "resize" && ev.target instanceof Window) {
            return;
        }

        this._shouldRefocus = true;
        this._togglePickList(false);

        if (ev.which === KeyCode.escape) {
            this._dropdownTitle.focus();
        }
    };

    private _onSelectionChanged = (selection: IPickListSelection<T>): boolean | void => {
        const isListOpen = this._getEffectiveIsListOpen() && this.props.selectionMode === SelectionMode.multiple;
        this._togglePickList(isListOpen);

        if (this._maintainsOwnState) {
            this.setState({
                selectedItems: selection.selectedItems
            });
        }

        if (this.props.onSelectionChanged) {
            return this.props.onSelectionChanged(selection);
        }
    };

    private _getNameFromItem = (item: any): string => {
        if (this.props.getListItem) {
            return this.props.getListItem(item).name;
        }
        return item;
    };

    private _getIconFromItem = (item: any): IVssIconProps | undefined => {
        if (this.props.getListItem) {
            return this.props.getListItem(item).iconProps;
        }
        return undefined;
    };

    private _togglePickList(isListOpen: boolean): void {
        const pickListWidth =
            this.props.pickListWidth || this.props.width || (this._dropdownTitle ? this._dropdownTitle.clientWidth : DEFAULT_PICKLIST_WIDTH);

        this.setState(
            {
                isListOpen: isListOpen,
                pickListWidth: pickListWidth
            },
            () => this.props.onToggleDropdown && this.props.onToggleDropdown(this.state.isListOpen)
        );
    }

    private _getEffectiveIsListOpen(): boolean {
        return this.props.isOpen !== undefined ? this.props.isOpen : this.state.isListOpen;
    }
}
